import { Pin, PinComponent } from '@lessonup/pins-shared';
import { useReducer } from 'react';
import { UpdatePinActionWithMetadata, UpdatePinDispatchWithMetadata } from './updatePinContext';
import { overridePinComponents, updateCategorySettings, updatePinSettings } from './useInMemoryPinListReducer.utils';

type State<P extends Pin> = P[];
type UpdateFunc<P extends Pin> = (statePin: P) => P;

function updatePin<P extends Pin>(state: State<P>, pinId: string | null, updater: UpdateFunc<P>): State<P> {
  if (!pinId) return state;
  return state.map((pin) => (pin.id === pinId ? updater(pin) : pin));
}

/**
 * Dev only in memory reducer for pin lists
 * TODO 2024-01-01: move this to storybook utils after deleting the teacher next dependency
 */
export function useInMemoryPinListReducer<P extends Pin>(initialPins: P[]): [State<P>, UpdatePinDispatchWithMetadata] {
  const [state, dispatch] = useReducer(pinListReducer<P>, initialPins);

  const dispatchWithMetadata: UpdatePinDispatchWithMetadata = (
    action: UpdatePinActionWithMetadata | UpdatePinActionWithMetadata[]
  ) => {
    dispatch(action);
  };

  return [state, dispatchWithMetadata];
}

// This is a very simple reducer that only supports view related updates
const pinListReducer = <P extends Pin>(
  state: State<P>,
  action: UpdatePinActionWithMetadata | UpdatePinActionWithMetadata[]
): State<P> => {
  const actionReducer = () => (state: State<P>, action: UpdatePinActionWithMetadata) => {
    switch (action.type) {
      case 'overridePinComponents':
        return updatePin(state, action.pinId, (state) => ({
          ...state,
          pinComponents: overridePinComponents(state.pinComponents, action.pinComponents),
        }));
      case 'updatePinComponentSettings':
        return updatePin(state, action.pinId, (state) => ({
          ...state,
          pinComponents: state.pinComponents.map((component) => {
            if (component.id === action.pinComponentId) {
              return {
                ...component,
                settings: {
                  ...component.settings,
                  ...action.settings,
                },
              } as PinComponent; // Cast is needed because it refuses to merge the union of pins
            }
            return component;
          }),
        }));
      case 'updateCategorySettings':
        return updatePin(state, action.pinId, (state) => ({
          ...state,
          pinComponents: updateCategorySettings(
            state.pinComponents,
            action.pinComponentId,
            action.categoryId,
            action.settings
          ),
        }));
      case 'updatePinSettings':
        return updatePin<P>(state, action.pinId, (state) => {
          return updatePinSettings(state, action.settings);
        });
      default:
        return state;
    }
  };
  const actions = Array.isArray(action) ? action : [action];
  return actions.reduce(actionReducer(), state);
};
